import { useEffect, useRef } from "react";

import { usePlayground } from "../../playground-store";

import { SaveCompletionTracker } from "./useAutoSaveSession";

import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useGeneratePlaygroundSessionName } from "@/queries";

const useNavigationSessionNameGuard = (
  saveCompletionTracker?: React.MutableRefObject<SaveCompletionTracker | null>,
) => {
  const playground = usePlayground();
  const userToken = useAuth()?.userToken || "";
  const { activeWorkspaceId } = useUser();
  const hasAssignedNameRef = useRef(false);
  const isGeneratingRef = useRef(false);

  const generateSessionName = useGeneratePlaygroundSessionName(userToken, {
    onSuccess: data => {
      playground.setSessionName(data.generated_name);
      hasAssignedNameRef.current = true;
      isGeneratingRef.current = false;
    },
    onError: () => {
      hasAssignedNameRef.current = true;
      isGeneratingRef.current = false;
    },
  });

  const ensureSessionName = async () => {
    if (
      hasAssignedNameRef.current ||
      isGeneratingRef.current ||
      playground.isPendingSessionInitialization ||
      playground.isInitializing ||
      playground.isXRayMode ||
      playground.sessionName ||
      !activeWorkspaceId ||
      !userToken
    ) {
      return;
    }

    // Wait for any pending saves to complete before generating name
    if (saveCompletionTracker?.current) {
      await saveCompletionTracker.current.waitForSaveCompletion();
    }

    isGeneratingRef.current = true;
    generateSessionName.mutate({
      workspace_id: activeWorkspaceId,
      playground_session_id: playground.instanceId,
      is_default: playground.hasDefaultMessages,
    });
  };

  useEffect(() => {
    hasAssignedNameRef.current = false;
    isGeneratingRef.current = false;
  }, [playground.instanceId]);

  useEffect(() => {
    // Handle browser navigation/close/refresh
    const handleBeforeUnload = (_event: BeforeUnloadEvent) => {
      ensureSessionName();
    };

    // Handle visibility change (tab switching, minimizing)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        ensureSessionName();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Ensure session name is set when component unmounts (navigation)
      ensureSessionName();

      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useNavigationSessionNameGuard;
